import { manualOrderRequiredChannels } from '@/constants';
import { AmiliHealthOrderStatus, KitStatus, OrderStatus } from '@/enums';
import { ChannelName } from '@/enums/channel';
import {
  KitEntity,
  KitStatusHistoryEntity,
} from '@/pages/privatePages/kit/types';
import { ProductGroupEntity } from '@/pages/privatePages/productgroup/types';
import { ChangeEvent } from 'react';
import z from 'zod';
import { orderItemSchema, rhfOrderSchema } from './Form/order.schema';

export const ORDER_STATUS_COLOR = {
  [OrderStatus.PENDING]: 'var(--link)',
  [OrderStatus.CONFIRMED]: 'var(--success)',
  [AmiliHealthOrderStatus.CREATED]: 'var(--secondary)',
  [AmiliHealthOrderStatus.COMPLETED]: 'var(--success)',
  [AmiliHealthOrderStatus.PROCESSED]: 'var(--warning)',
  [AmiliHealthOrderStatus.DELIVERED]: 'var(--link)',
  [AmiliHealthOrderStatus.CANCELLED]: 'var(--error)',
  [AmiliHealthOrderStatus.RECEIVED]: 'var(--primary)',
};

export const getScrollY = (
  props: { hasTabs?: boolean; height?: number; more?: number } = {},
) => {
  const { hasTabs, height = 0, more = 0 } = props;
  const DEFAULT_PAGE_HEIGHT = 223;
  const TABS_HEIGHT = 40;
  let pageHeight = DEFAULT_PAGE_HEIGHT + height;
  if (hasTabs) pageHeight += TABS_HEIGHT + 1;
  const header = document.querySelector('.ant-table-header');
  const headerHeight = header?.getBoundingClientRect().height || 40;

  return `calc(100vh - (${pageHeight}px + ${headerHeight}px)${
    ' + ' + more + 'px' || ''
  })`;
};

export const checkKitClinicReceived = (
  statusHistory: KitStatusHistoryEntity[] = [],
) => {
  return statusHistory.some(
    history => history.status === KitStatus.CLINIC_RECEIVED,
  );
};

export const checkAllKitsClinicReceived = (kits: KitEntity[]) => {
  return !kits.some(kit => !checkKitClinicReceived(kit.statusHistory));
};

export const saveOrderToDraft = (
  values: z.infer<typeof rhfOrderSchema>,
  key?: string,
) => {
  if (!key) return;
  const draft = localStorage.getItem(key);
  const stringifiedValues = JSON.stringify(values);

  if (draft === stringifiedValues) return;
  localStorage.setItem(key, stringifiedValues);
};

export const checkManualOrderRequiredChannel = (channelName?: string) => {
  if (!channelName) return false;
  return manualOrderRequiredChannels.includes(channelName as ChannelName);
};

export const calculateProductGroupPrice = (
  productGroupData: ProductGroupEntity[] | undefined,
  orderItems: z.infer<typeof orderItemSchema>[],
  formItem: z.infer<typeof orderItemSchema>,
) => {
  const productId = formItem.productId;

  const group = productGroupData?.find(group =>
    group.productIds.includes(productId),
  );

  if (!group) return formItem.productPrice ?? 0;

  const itemsInGroup = orderItems.filter(item =>
    group.productIds.includes(item?.productId || ''),
  );

  const totalQuantity = itemsInGroup.reduce(
    (acc, item) => acc + Number(item.quantity || 0),
    0,
  );

  return (
    group.priceRange?.find(
      range =>
        range.quantityFrom <= totalQuantity &&
        (range.quantityTo ?? Infinity) >= totalQuantity,
    )?.price ??
    formItem.productPrice ??
    0
  );
};

export const handleTypingSerial = (e: ChangeEvent<HTMLInputElement>) => {
  const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, '').substring(0, 12);

  const parts = cleaned.match(/.{1,4}/g) ?? [];

  e.target.value = parts.join('-').toUpperCase();
};
